import { Connective, IFilterGroup, IQueryFilterWrapper } from '../interfaces/query-filter.interface';

type FilterShape = IFilterGroup | IQueryFilterWrapper[] | { connective?: Connective; expressions?: IQueryFilterWrapper[] } | null | undefined;

export const ROOT_CONNECTIVE: Connective = 'and';
export const GROUP_CONNECTIVE: Connective = 'or';

export function filterGroups<T = IQueryFilterWrapper>(filters: { expressions?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(filters)) {
    return filters;
  }
  return filters?.expressions || [];
}

function isConnective(value: unknown): value is Connective {
  return value === ROOT_CONNECTIVE || value === GROUP_CONNECTIVE;
}

export function rootConnective(filters: { connective?: Connective }): Connective {
  const connective = filters?.connective;
  return isConnective(connective) ? connective : ROOT_CONNECTIVE;
}

export function groupConnective(group: { connective?: Connective }): Connective {
  const connective = group?.connective;
  return isConnective(connective) ? connective : GROUP_CONNECTIVE;
}

export function oppositeConnective(connective: Connective): Connective {
  return connective === ROOT_CONNECTIVE ? GROUP_CONNECTIVE : ROOT_CONNECTIVE;
}

export function impliedRootConnective(groups: { connective?: Connective }[]): Connective {
  return oppositeConnective(groupConnective(groups.find(group => isConnective(group?.connective))));
}

export function setInnerConnectives(groups: { connective?: Connective }[], topConnective: Connective): void {
  groups.forEach(group => group.connective = oppositeConnective(topConnective));
}

export function withImpliedConnective<T extends { connective?: Connective }>(groups: T[]): T[] & { connective: Connective } {
  return Object.assign(groups, { connective: impliedRootConnective(groups) });
}

export function asFilterObject<T>(filters: { connective?: Connective } | null | undefined, expressions: T[]): { connective: Connective, expressions: T[] } {
  return { connective: rootConnective(filters), expressions };
}

export function filterHasExpressions(filter: FilterShape): boolean {
  if (!filter) {
    return false;
  }
  if (Array.isArray(filter)) {
    return filter.length > 0;
  }
  return !!filter.expressions?.length;
}

export function serializeFilterGroups<T extends IQueryFilterWrapper>(groups: T[]) {
  return (groups || []).map(group => ({
    ...group,
    connective: groupConnective(group),
    expressions: group.expressions.map((expression: any) => (expression?.asJson ? expression.asJson() : expression)),
  }));
}

export function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}
